import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsPage } from "~/components/settings/SettingsPage";
import { useProfileSave } from "~/components/settings/use-profile-save";
import { useAuthStore } from "~/store/auth-store";

/**
 * The member's display name.
 *
 * The only setting here with a Save button: a text field has no natural moment to
 * commit, and saving on every keystroke would be a request per character.
 */
export function UsernameRoute() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { save, saving, status, dismiss } = useProfileSave();

  const [displayName, setDisplayName] = useState(user?.display_name ?? "");

  if (!user) return null;

  const trimmed = displayName.trim();
  // An empty field clears the name, which is a legitimate change — but only if there
  // was one to clear.
  const unchanged = trimmed === (user.display_name ?? "");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await save({ display_name: trimmed || null });
  };

  return (
    <SettingsPage
      title={t("profile.username.title")}
      status={status}
      onDismiss={dismiss}
      onSubmit={submit}
      action={
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={saving || unchanged}
          data-testid="profile.save"
        >
          {t("common.save")}
        </Button>
      }
    >
      <TextField
        label={t("profile.username.label")}
        placeholder={t("profile.username.placeholder")}
        value={displayName}
        onChange={(event) => setDisplayName(event.target.value)}
        fullWidth
        autoComplete="name"
        slotProps={{
          htmlInput: {
            "data-testid": "profile.display-name",
            maxLength: 255,
          },
        }}
      />
    </SettingsPage>
  );
}
